import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import ServiceCard from "@/components/services/ServiceCard";
import { Code, FileText, Network, Database as DatabaseIcon, Share2, Shield } from "lucide-react";
import ContactForm from "../components/ContactForm";
import LatestNews from "../components/LatestNews";
import AboutSection from "../components/AboutSection";
import SolutionsSection from "../components/SolutionsSection";
import Subscribe from "../components/Subscribe";

type Service = Database['public']['Tables']['services']['Row'];

const Home = () => {
  const { data: services, isLoading } = useQuery({
    queryKey: ["home-services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("id, title, description, slug, features")
        .limit(4);

      if (error) throw error;
      return data as Service[];
    }
  });

  const getIconForService = (title: string) => {
    switch (title.toLowerCase()) {
      case "software development":
        return <Code className="h-10 w-10 text-indigo-500" />;
      case "it infrastructure":
        return <Network className="h-10 w-10 text-blue-500" />;
      case "cloud services":
        return <DatabaseIcon className="h-10 w-10 text-purple-500" />;
      case "erp solutions":
        return <FileText className="h-10 w-10 text-green-500" />;
      case "cyber security":
        return <Shield className="h-10 w-10 text-red-500" />;
      default:
        return <Share2 className="h-10 w-10 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 mb-6">
              Welcome to SSL Solution
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Your Trusted Partner for Business Solutions and Services
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/services">
                <Button size="lg" className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
                  Explore Our Services
                </Button>
              </Link>
              <Link to="/solutions">
                <Button size="lg" variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50">
                  View Solutions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AboutSection />

      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-purple-800 dark:text-purple-200 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              We offer a wide range of services to help your business grow and succeed
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {isLoading ? (
              <div className="col-span-4 text-center py-8">Loading services...</div>
            ) : services && services.length > 0 ? (
              services.map((service) => (
                <ServiceCard
                  key={service.id}
                  id={service.id}
                  title={service.title}
                  description={service.description}
                  icon={getIconForService(service.title)}
                  features={service.features}
                />
              ))
            ) : (
              <div className="col-span-4 text-center py-8">No services found</div>
            )}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SolutionsSection />

      <LatestNews />

      <ContactForm />

      <section className="bg-gradient-to-r from-purple-100 to-indigo-100 dark:from-gray-800 dark:to-gray-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-purple-800 dark:text-purple-200 mb-4">
              Stay Updated
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Subscribe to our newsletter for the latest updates, insights, and special offers.
            </p>
            <Subscribe className="flex justify-center" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
